import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react'

export const imageEnglish = {
  attach: 'Add image', send: 'Send image', remove: 'Remove image', preview: 'Image preview',
  limit: 'JPG, PNG or WebP · up to 5 MB', invalid: 'Choose a JPG, PNG or WebP image smaller than 5 MB.',
  failed: 'Image was not sent. Your preview is still here; try again.', sending: 'Sending image…',
  view: 'View image', close: 'Close image', loading: 'Loading image…', loadFailed: 'Could not load the image. Try again.',
}
export type ImageText = keyof typeof imageEnglish
export const imageArabic: Record<ImageText, string> = {
  attach: 'إضافة صورة', send: 'إرسال الصورة', remove: 'حذف الصورة', preview: 'معاينة الصورة',
  limit: 'JPG أو PNG أو WebP · حتى ٥ ميجابايت', invalid: 'اختر صورة JPG أو PNG أو WebP أصغر من ٥ ميجابايت.',
  failed: 'لم يتم إرسال الصورة. المعاينة موجودة؛ حاول مرة أخرى.', sending: 'جارٍ إرسال الصورة…',
  view: 'عرض الصورة', close: 'إغلاق الصورة', loading: 'جارٍ تحميل الصورة…', loadFailed: 'تعذر تحميل الصورة. حاول مرة أخرى.',
}
const english = (key: ImageText): string => imageEnglish[key]

export function ImageAttachment({ upload, disabled = false, text = english }: {
  upload: (file: File) => Promise<void>; disabled?: boolean; text?: (key: ImageText) => string
}): ReactElement {
  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ImageText | null>(null)
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  const remove = () => { setFile(null); setUrl(''); setError(null); if (input.current) input.current.value = '' }
  const choose = (selected?: File) => {
    if (!selected) return
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(selected.type) || selected.size > 5 * 1024 * 1024 || !selected.size) {
      setError('invalid'); if (input.current) input.current.value = ''; return
    }
    setFile(selected); setUrl(URL.createObjectURL(selected)); setError(null)
  }
  const send = async () => {
    if (!file || busy) return
    setBusy(true); setError(null)
    try { await upload(file); remove() } catch { setError('failed') } finally { setBusy(false) }
  }
  return <div className="support-attachment">
    <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" hidden aria-label={text('attach')} disabled={disabled || busy} onChange={(event) => choose(event.target.files?.[0])} />
    {!file && <button type="button" className="btn btn-secondary btn-sm" title={text('limit')} disabled={disabled || busy} onClick={() => input.current?.click()}>
      <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><rect x="3" y="3" width="18" height="18" rx="4" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m3 17 5-5 4 4 4-6 5 7" /></svg>
      {text('attach')}
    </button>}
    {file && <div className="support-attachment-preview">
      <img className="support-image" src={url} alt={text('preview')} />
      <p className="mb-2 truncate text-xs text-ink-2">{file.name}</p>
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-primary btn-sm" disabled={busy || disabled} onClick={() => { void send() }}>{busy ? text('sending') : text('send')}</button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={remove}>{text('remove')}</button>
      </div>
    </div>}
    {error && <p className="mt-2 text-sm text-danger" role="alert">{text(error)}</p>}
  </div>
}

export function ImageMessage({ load, text = english }: { load: () => Promise<Blob>; text?: (key: ImageText) => string }): ReactElement {
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  const viewer = useRef<HTMLDialogElement>(null)
  const container = useRef<HTMLDivElement>(null)
  const loader = useRef(load)
  loader.current = load
  const alive = useRef(true)
  useEffect(() => { alive.current = true; return () => { alive.current = false } }, [])
  useEffect(() => () => { if (url) URL.revokeObjectURL(url) }, [url])
  const open = useCallback(async () => {
    setBusy(true); setError(false)
    try { const blob = await loader.current(); if (alive.current) setUrl(URL.createObjectURL(blob)) }
    catch { if (alive.current) setError(true) }
    finally { if (alive.current) setBusy(false) }
  }, [])
  useEffect(() => {
    const element = container.current
    if (!element) return
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); void open() }
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [open])
  return <div ref={container}>
    {url ? <>
      <button type="button" className="support-image-open" aria-label={text('view')} onClick={() => viewer.current?.showModal()}>
        <img src={url} className="support-image" alt={text('preview')} />
        <span className="support-image-zoom" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M3 3l6 6m12-6-6 6M3 21l6-6m12 6-6-6" /></svg></span>
      </button>
      <dialog ref={viewer} className="support-image-viewer" aria-label={text('view')}>
        <div className="support-viewer-header"><p className="text-sm font-semibold">{text('preview')}</p><button type="button" className="btn btn-secondary btn-sm" onClick={() => viewer.current?.close()}>{text('close')}</button></div>
        <img src={url} alt={text('preview')} className="support-viewer-image" />
      </dialog>
    </>
      : <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => { void open() }}>{busy ? text('loading') : text('view')}</button>}
    {error && <p role="alert" className="mt-2 text-xs">{text('loadFailed')}</p>}
  </div>
}
